// useStickyControl — the control you just clicked stays under the pointer.
//
// Owner, 2026-09-04: "may it leave that selector at place and bump upper part up?" Ticking the
// first district moved the checkbox ~90px down the screen, so the thing you just clicked is no
// longer where you clicked it and the row you were reading has moved.
//
// 🔑 WHAT THREE FAILED ATTEMPTS TAUGHT, all of it measured rather than reasoned:
//   • It is NOT the amber "hidden for missing data" line — that does not render for every view.
//   • It is NOT the saved-search chip row — md:absolute, contributes no height on desktop.
//   • It is NOT the strip's mb-7 reserve — making it unconditional changed nothing isolable and
//     cost permanent whitespace.
//   • The real mover is the panel's own "what you're searching" readout, +82px, WHILE the source
//     list below it shrank 29px in the same commit.
//   • And decisively: THE PAGE DOES NOT SCROLL, THE PANEL DOES. It is a max-h card with its own
//     overflow (scrollHeight 3416 vs clientHeight 818), so every correction written against
//     window.scrollY moved nothing — which is exactly what `scrollCompensated: 0` kept reporting.
//
// So this anchors the ELEMENT rather than compensating a height. Giving back one block's growth
// is wrong whenever two things move in opposite directions, which is the actual case here.
//
// ⚠️ It watches for a WINDOW after the click rather than reacting to one resize, because the
// layout changes when the RESULTS land — hundreds of milliseconds later, in a different render,
// and sometimes in two steps. A single ResizeObserver callback fires before the second step.
// ⚠️ A 16ms TIMER, not requestAnimationFrame, and that is deliberate. rAF does not fire while
// the page is not compositing — a hidden tab, a background window, or (found here) a browser
// pane that is not displayed. With rAF the correction silently never runs in exactly the
// conditions where it also cannot be TESTED, which is how a mechanism ships unverified. A
// bounded timer runs either way, and 1.8s of 16ms ticks is negligible.
// ⚠️ It yields to the human immediately: any wheel, touch or key scroll ends the window. The
// agent's own scrolling must always win over a correction.
import { useEffect } from 'react'

const HOLD_MS = 1800   // long enough for a search to land, short enough to never feel sticky

/**
 * @param {object} scrollerRef  ref to the element that actually scrolls (the panel card)
 * @param {object} opts
 * @param {boolean} opts.enabled
 */
export function useStickyControl (scrollerRef, { enabled = true } = {}) {
  useEffect(() => {
    const scroller = scrollerRef.current
    if (!enabled || !scroller) return

    let anchor = null      // { el, top }
    let until = 0
    let raf = 0

    const release = () => { anchor = null; until = 0; if (raf) clearTimeout(raf); raf = 0 }

    // The human always wins.
    const yieldToUser = () => release()

    const tick = () => {
      raf = 0
      if (!anchor || performance.now() > until) return release()
      if (!anchor.el.isConnected) return release()
      const now = anchor.el.getBoundingClientRect().top
      const drift = now - anchor.top
      // Sub-pixel drift is layout noise, not movement.
      if (Math.abs(drift) >= 1) {
        const next = scroller.scrollTop + drift
        const max = scroller.scrollHeight - scroller.clientHeight
        scroller.scrollTop = Math.max(0, Math.min(max, next))
      }
      raf = setTimeout(tick, 16)
    }

    const onPointerDown = (e) => {
      const el = e.target && e.target.closest && e.target.closest('input,button,label,select,a')
      if (!el || !scroller.contains(el)) return
      anchor = { el, top: el.getBoundingClientRect().top }
      until = performance.now() + HOLD_MS
      if (!raf) raf = setTimeout(tick, 16)
    }

    scroller.addEventListener('pointerdown', onPointerDown, true)
    scroller.addEventListener('wheel', yieldToUser, { passive: true })
    scroller.addEventListener('touchmove', yieldToUser, { passive: true })
    window.addEventListener('keydown', yieldToUser, true)
    return () => {
      release()
      scroller.removeEventListener('pointerdown', onPointerDown, true)
      scroller.removeEventListener('wheel', yieldToUser)
      scroller.removeEventListener('touchmove', yieldToUser)
      window.removeEventListener('keydown', yieldToUser, true)
    }
  }, [scrollerRef, enabled])
}

/**
 * The pure half, so the correction is testable without a browser: given where the control was
 * and where it is now, how far should the scroller move, clamped to its own range?
 * Extracted because the three failed attempts were all UNTESTABLE — the logic lived inside an
 * effect that only a rendered page could reach.
 */
export function scrollCorrection ({ wasTop, nowTop, scrollTop, scrollHeight, clientHeight }) {
  const drift = nowTop - wasTop
  if (!Number.isFinite(drift) || Math.abs(drift) < 1) return scrollTop
  const max = Math.max(0, scrollHeight - clientHeight)
  return Math.max(0, Math.min(max, scrollTop + drift))
}
